import { Component, OnInit, Renderer2, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { APP_LANGUAGE_KEY, LANGUAGE } from '@constants/app.constant';
import { ToastDirective } from '@directives/toast.directive';
import { StoreModel } from '@models/store.model';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDetectService } from '@services/device-detect.service';
import { ToastService } from '@services/toast.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ToastModule } from 'primeng/toast';
import { combineLatest, distinctUntilChanged, filter, fromEvent, map, withLatestFrom } from 'rxjs';
import { loadAirlineDDLAction, loadAirPortDDLAction } from './store/actions/common.action';
import { NgProgress, NgProgressModule, NgProgressRef } from 'ngx-progressbar';

@Component({
    selector: 'pn-root',
    standalone: true,
    imports: [RouterOutlet, ToastDirective, ToastModule, NgProgressModule],
    template: `
        <ng-progress
            id="nnpn-progress"
            [debounceTime]="0"
            direction="ltr+"
            [spinner]="false"
        ></ng-progress>
        <p-toast pnToast [position]="isMobile() ? 'top-center' : 'top-right'"></p-toast>
        <router-outlet></router-outlet>
    `,
    styles: ``,
})
export class AppComponent implements OnInit {
    public isMobile: Signal<boolean | undefined>;
    public isVisible = false;
    public progressRef!: NgProgressRef;

    constructor(
        private _render: Renderer2,
        private _deviceService: DeviceDetectorService,
        private device: DeviceDetectService,
        private translateService: TranslateService,
        private _route: ActivatedRoute,
        private _router: Router,
        private toastService: ToastService,
        private titleService: Title,
        private store: Store<StoreModel>,
        private ngProgress: NgProgress,
    ) {
        this.isMobile = toSignal(this.device.isMobile());
        const fnName = this._deviceService.isMobile() ? 'addClass' : 'removeClass';
        this._render[fnName](document.documentElement, 'mobile-device');

        const titleByRouter = this._router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => {
                let route = this._route;
                while (route.firstChild) {
                    route = route.firstChild;
                }
                return route.snapshot.data['title'];
            })
        );
        combineLatest({ titleByRouter, language: this.translateService.onLangChange })
            .pipe(
                filter(({ titleByRouter }) => !!titleByRouter || typeof titleByRouter === 'string')
            )
            .subscribe(({ titleByRouter }) => {
                this.titleService.setTitle(this.translateService.instant(titleByRouter));
            });

        const loading$ = this.store
            .select((state) => state.common.loading)
            .pipe(
                map((value) => {
                    return value > 0;
                }),
                distinctUntilChanged()
            );

        this.progressRef = this.ngProgress.ref('nnpn-progress');
        loading$.subscribe((value) => {
            if (value) {
                this.progressRef.start();
            } else {
                this.progressRef.complete();
            }
        });

        const updateLoading$ = this.store
            .select((state) => state.common.updateLoading)
            .pipe(
                map((value) => {
                    return value > 0;
                }),
                distinctUntilChanged()
            );

        fromEvent(document, 'click', { capture: true })
            .pipe(
                withLatestFrom(updateLoading$),
                filter(([_, loading]) => loading)
            )
            .subscribe(([event]) => {
                event.preventDefault();
                event.stopPropagation();
            });
    }

    ngOnInit(): void {
        this.translateService.setDefaultLang(LANGUAGE.VI);
        const language = localStorage.getItem(APP_LANGUAGE_KEY);
        this.translateService.use(language || LANGUAGE.VI);
        this.store.dispatch(loadAirPortDDLAction());
        this.store.dispatch(loadAirlineDDLAction());
    }

    /**
     * Close toast
     *
     * @memberof AppComponent
     */
    public onCloseToast() {
        this.toastService.clear('network-connection');
        this.isVisible = false;
    }
}
